/**
 * Durable manual-dismiss store for automation steps.
 *
 * Survives "Clear local automation state" so Stop / close during an active
 * window does not immediately re-fire via catch-up after a ledger reset.
 * Identities are date-scoped and pruned after a few days.
 */

export const AUTOMATION_MANUAL_DISMISSALS_STORAGE_KEY = "automationManualDismissals";
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

const canUseStorage = () =>
  typeof window !== "undefined" && Boolean(window.localStorage);

const readStore = () => {
  if (!canUseStorage()) return {};
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(AUTOMATION_MANUAL_DISMISSALS_STORAGE_KEY) || "{}"
    );
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (store) => {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(
      AUTOMATION_MANUAL_DISMISSALS_STORAGE_KEY,
      JSON.stringify(store || {})
    );
  } catch {
    // no-op
  }
};

/** Stable identity: survives class-time / event-key churn within the same day. */
export const buildManualDismissalIdentity = ({
  dateKey,
  sequenceId,
  stepId,
  classId,
  eventKey,
} = {}) => {
  if (dateKey && sequenceId && stepId) {
    return [dateKey, sequenceId, stepId, classId || "no-class"].join("|");
  }
  return String(eventKey || "").trim();
};

export const buildManualDismissalIdentityFromEvent = (event = {}) =>
  buildManualDismissalIdentity({
    dateKey: event.dateKey,
    sequenceId: event.sequenceId,
    stepId: event.stepId,
    classId: event.classId,
    eventKey: event.key,
  });

const pruneStore = (store = {}, nowMs = Date.now()) => {
  const next = {};
  Object.entries(store).forEach(([identity, entry]) => {
    const dismissedAtMs = Date.parse(entry?.dismissedAt || "");
    if (!Number.isFinite(dismissedAtMs) || nowMs - dismissedAtMs > MAX_AGE_MS) {
      return;
    }
    next[identity] = entry;
  });
  return next;
};

export const recordManualDismissal = (event = {}, { reason = "manual" } = {}) => {
  const identity = buildManualDismissalIdentityFromEvent(event);
  if (!identity) return null;

  const store = pruneStore(readStore());
  store[identity] = {
    identity,
    dateKey: event.dateKey || "",
    sequenceId: event.sequenceId || "",
    stepId: event.stepId || "",
    classId: event.classId || "",
    eventKey: event.key || "",
    stepType: event.type || "",
    reason,
    dismissedAt: new Date().toISOString(),
  };
  writeStore(store);
  return store[identity];
};

export const isManuallyDismissedEvent = (event = {}) => {
  const identity = buildManualDismissalIdentityFromEvent(event);
  if (!identity) return false;
  const raw = readStore();
  const store = pruneStore(raw);
  if (Object.keys(store).length !== Object.keys(raw).length) {
    writeStore(store);
  }
  return Boolean(store[identity]);
};

export const isManuallyDismissedIdentity = (identity = "") => {
  if (!identity) return false;
  const store = pruneStore(readStore());
  return Boolean(store[identity]);
};

/** Test / explicit full wipe only — not used by Clear local automation state. */
export const clearManualDismissals = () => {
  if (!canUseStorage()) return;
  try {
    window.localStorage.removeItem(AUTOMATION_MANUAL_DISMISSALS_STORAGE_KEY);
  } catch {
    // no-op
  }
};
